import CreateRubricAIDialog from 'components/CreateAssignment/CreateRubricAIDialog';
import CreateRubricDialog from 'components/CreateAssignment/CreateRubricDialog';
import CreateRubricExistingDialog from 'components/CreateAssignment/CreateRubricExistingDialog';
import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import EditGradingSettingsDialog from 'components/CreateAssignment/EditGradingSettingsDialog';
import EditRubricDialog from 'components/CreateAssignment/EditRubricDialog';
import FeedbackSettingsDialog from 'components/CreateAssignment/FeedbackSettingsDialog';
import ImportAssignmentSettingsDialog from 'components/CreateAssignment/ImportAssignmentSettingsDialog';
import InlineCommentsSettingsDialog from 'components/CreateAssignment/InlineCommentsSettingsDialog';
import StudentFeedbackSettingsDialog from 'components/CreateAssignment/StudentFeedbackSettingsDialog';
import UploadContentBankResourcesDialog from 'components/CreateAssignment/UploadContentBankResourcesDialog';
import PushRubricDialog from 'components/InstructorAssignments/PushRubricDialog';
import SelectRubricDialog from 'components/Rubrics/SelectRubricDialog';
import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { cloneDeep, omit } from 'lodash';
import Desktop from 'pages/CreateAssignment/desktop';
import Mobile from 'pages/CreateAssignment/mobile';
import { PageContext as CreatePageContext } from 'pages/CreateAssignment/PageProvider';
import Tablet from 'pages/CreateAssignment/tablet';
import { PageContext as EditPageContext } from 'pages/EditAssignment/PageProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import { Loading, VideoDialog } from 'shell/components';
import { applyAssignmentRollover, ROLLOVER_GROUPS } from 'utils/assignmentRollover';
import { inferSubmissionTypes } from 'utils/assignmentSubmissionTypes';
import { mergeGradingSettings } from 'utils/gradingInstructions';
import { convertCanvasRubric, convertD2LRubric } from 'utils/rubricScoring';

const PageLayout = ({ location }) => {
    const {
        isLoading,
        assignments,
        batch,
        submissions,
        setSubmissions,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
        isRubricFeaturedDisabled,
    } = useApp();
    const { setNode } = useAppBar();
    const { auth } = useAuth();
    const params = useParams();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const [videoDialog, setVideoDialog] = useState({ open: false, videoId: '' });
    const [importAssignmentSettingsDialog, setImportAssignmentSettingsDialog] = useState({ open: false });
    const [forceRTEUpdate, setForceRTEUpdate] = useState(false);
    const [settingsRevision, setSettingsRevision] = useState(0);
    const onSettingsReplaced = useCallback(() => setSettingsRevision((value) => value + 1), []);
    const PageContext = location.pathname === '/assignments/new' ? CreatePageContext : EditPageContext;
    const props = useContext(PageContext);
    const {
        setLoading,
        form,
        setForm,
        setErrors,
        selectRubricDialog,
        setSelectRubricDialog,
        createRubricDialog,
        setCreateRubricDialog,
        createRubricExistingDialog,
        setCreateRubricExistingDialog,
        createRubricAIDialog,
        setCreateRubricAIDialog,
        editRubricDialog,
        setEditRubricDialog,
        uploadContentBankResourcesDialog,
        setUploadContentBankResourcesDialog,
        editContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        gradingSettingsDialog,
        setGradingSettingsDialog,
        feedbackSettingsDialog,
        setFeedbackSettingsDialog,
        studentFeedbackSettingsDialog,
        setStudentFeedbackSettingsDialog,
        inlineCommentsSettingsDialog,
        setInlineCommentsSettingsDialog,
        pushRubricDialog,
        setPushRubricDialog,
        setIsSaving,
    } = props;

    const assignment = useMemo(() => assignments?.find(({ id }) => id === params.id), [assignments, params.id]);

    const openVideoAssignmentCreation = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'zSIaLCoDNfQ' });
    }, []);

    const openVideoFeedbackSettings = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'fdUqnMyB6Rc' });
    }, []);

    const openVideoGradingSettings = () => {
        // video doesn't exist yet, replace with actual implementation when the video is made
        // setVideoDialog({ open: true, videoId: '' });
    };

    const closeSelectRubricDialog = useCallback(() => setSelectRubricDialog({ open: false }), [setSelectRubricDialog]);

    const handleClickAddRubric = useCallback(() => setCreateRubricDialog({ open: true }), [setCreateRubricDialog]);

    const handleClickUseRubric = useCallback(
        (rubric) => setEditRubricDialog({ open: true, customDefaultForm: rubric.rubric }),
        [setEditRubricDialog]
    );

    const closeCreateRubricDialog = useCallback(() => setCreateRubricDialog({ open: false }), [setCreateRubricDialog]);

    const handleClickCreateExisting = useCallback(
        () => setCreateRubricExistingDialog({ open: true }),
        [setCreateRubricExistingDialog]
    );

    const handleClickCreateAI = useCallback(() => setCreateRubricAIDialog({ open: true }), [setCreateRubricAIDialog]);

    const handleClickCreateManual = useCallback(
        () => setEditRubricDialog({ open: true, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const closeCreateRubricExistingDialog = useCallback(
        () => setCreateRubricExistingDialog({ open: false }),
        [setCreateRubricExistingDialog]
    );

    const closeCreateRubricAIDialog = useCallback(
        () => setCreateRubricAIDialog({ open: false }),
        [setCreateRubricAIDialog]
    );

    const closeEditRubricDialog = useCallback(
        () => setEditRubricDialog({ open: false, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const handleSaveRubric = useCallback(
        (rubric) => {
            setForm((current) => {
                const criterionIds = rubric.criteria.map(({ id }) => id);
                let newForm = cloneDeep({ ...current, rubric });

                if (current.feedbackSettings.structure === 'templated') {
                    let newTemplated = cloneDeep(current.feedbackSettings.templated);

                    // if rubric criteria or options were removed, remove them from feedbackSettings.templated
                    Object.keys(newTemplated).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newTemplated[criterionId];
                        } else {
                            const optionIds = rubric.criteria
                                .find(({ id }) => id === criterionId)
                                .options.map(({ id }) => id);
                            Object.keys(newTemplated[criterionId]).forEach((optionId) => {
                                if (!optionIds.includes(optionId)) {
                                    delete newTemplated[criterionId][optionId];
                                }
                            });
                        }
                    });

                    // check if any criteria or options were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId, options }) => {
                        return (
                            Object.keys(newTemplated).includes(criterionId) &&
                            options.every(({ id: optionId }) => {
                                return Object.keys(newTemplated[criterionId]).includes(optionId);
                            })
                        );
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({
                            ...current,
                            feedbackSettings: `(${t('needs-update', 'needs update')})`,
                        }));
                    }

                    newForm.feedbackSettings.templated = newTemplated;
                }

                let newGradingSettings = cloneDeep(current.gradingSettings);
                if (newGradingSettings?.criteria) {
                    // remove keys that have been removed from the rubric
                    Object.keys(newGradingSettings.criteria).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newGradingSettings.criteria[criterionId];
                        }
                    });

                    // check if any criteria were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId }) => {
                        return Object.keys(newGradingSettings.criteria).includes(criterionId);
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({
                            ...current,
                            gradingSettings: `(${t('needs-update', 'needs update')})`,
                        }));
                    }
                }

                if (newGradingSettings?.criteria)
                    newForm.gradingSettings = mergeGradingSettings(newGradingSettings, newGradingSettings.criteria);
                return newForm;
            });
        },
        [setErrors, setForm, t]
    );

    const closeUploadContentBankResourcesDialog = useCallback(
        () => setUploadContentBankResourcesDialog({ open: false }),
        [setUploadContentBankResourcesDialog]
    );

    const onClickUploadContentBankResources = useCallback(
        (files) => {
            setForm((current) => ({
                ...current,
                contentBankResources: [...current.contentBankResources, ...files],
            }));
            setEditContentBankResourcesDialog({ open: true });
        },
        [setEditContentBankResourcesDialog, setForm]
    );

    const closeEditContentBankResourcesDialog = useCallback(
        () => setEditContentBankResourcesDialog({ open: false }),
        [setEditContentBankResourcesDialog]
    );

    const handleSaveContentBankResources = useCallback(
        (contentBankResources) => setForm((current) => ({ ...current, contentBankResources })),
        [setForm]
    );

    const closeGradingSettingsDialog = useCallback(
        () => setGradingSettingsDialog({ open: false }),
        [setGradingSettingsDialog]
    );

    const handleSaveGradingSettings = useCallback(
        (value) => {
            const { gradingCustomContext, criteria } = value;
            setForm((current) => {
                return {
                    ...current,
                    gradingCustomContext,
                    gradingSettings: mergeGradingSettings(current.gradingSettings, criteria),
                };
            });
            setErrors((current) => omit(current, ['gradingSettings']));
        },
        [setErrors, setForm]
    );

    const closeFeedbackSettingsDialog = useCallback(
        () => setFeedbackSettingsDialog({ open: false }),
        [setFeedbackSettingsDialog]
    );

    const handleSaveFeedbackSettings = useCallback(
        (value) => {
            const { feedbackCustomContext } = value;
            value = omit(value, ['feedbackCustomContext']);
            setForm((current) => {
                return {
                    ...current,
                    feedbackSettings: value,
                    aiGradingEnabled: true,
                    feedbackCustomContext,
                };
            });
            setErrors((current) => omit(current, ['feedbackSettings']));
        },
        [setErrors, setForm]
    );

    const closeStudentFeedbackSettingsDialog = useCallback(
        () => setStudentFeedbackSettingsDialog({ open: false }),
        [setStudentFeedbackSettingsDialog]
    );

    const handleSaveStudentFeedbackSettings = useCallback(
        (value) => {
            const { studentFeedbackCustomContext } = value;
            value = omit(value, ['studentFeedbackCustomContext']);
            setForm((current) => {
                return {
                    ...current,
                    studentFeedbackSettings: value,
                    aiGradingEnabled: true,
                    studentFeedbackCustomContext,
                };
            });
            setErrors((current) => omit(current, ['studentFeedbackSettings']));
        },
        [setErrors, setForm]
    );

    const closeInlineCommentsSettingsDialog = useCallback(
        () => setInlineCommentsSettingsDialog({ open: false }),
        [setInlineCommentsSettingsDialog]
    );

    const handleSaveInlineCommentsSettings = useCallback(
        (value) => {
            const { inlineCommentsCustomContext } = value;
            value = omit(value, ['inlineCommentsCustomContext']);
            setForm((current) => {
                return {
                    ...current,
                    inlineCommentsSettings: value,
                    inlineCommentsCustomContext,
                };
            });
        },
        [setForm]
    );

    const closePushRubricDialog = useCallback(() => {
        setIsSaving(false);
        if (setLoading) setLoading(false);
        setPushRubricDialog({ open: false });
    }, [setIsSaving, setLoading, setPushRubricDialog]);

    const closeVideoDialog = useCallback(() => setVideoDialog({ open: false, videoId: '' }), []);

    const rolloverDisabledGroups = useMemo(
        () =>
            [
                isRubricFeaturedDisabled && ROLLOVER_GROUPS.RUBRIC,
                isGradingFeatureDisabled && ROLLOVER_GROUPS.GRADING,
                isFeedbackFeatureDisabled && ROLLOVER_GROUPS.FEEDBACK,
                isFeedbackFeatureDisabled && ROLLOVER_GROUPS.FORMATIVE,
                isInlineCommentsFeatureDisabled && ROLLOVER_GROUPS.INLINE_COMMENTS,
            ].filter(Boolean),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled, isInlineCommentsFeatureDisabled, isRubricFeaturedDisabled]
    );

    const handleImportAssignmentSettings = useCallback(
        ({ gradingMapping, selectedGroups, source }) => {
            if (selectedGroups.includes(ROLLOVER_GROUPS.GRADING)) onSettingsReplaced();
            setForm((current) =>
                applyAssignmentRollover({
                    targetForm: current,
                    sourceAssignment: source,
                    selectedGroups,
                    disabledGroups: rolloverDisabledGroups,
                    gradingMapping,
                })
            );
            setErrors((current) => {
                const resolved = omit(
                    current,
                    [
                        selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION) && 'description',
                        selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) && 'rubric',
                        selectedGroups.includes(ROLLOVER_GROUPS.GRADING) && 'gradingSettings',
                        selectedGroups.includes(ROLLOVER_GROUPS.FEEDBACK) && 'feedbackSettings',
                        selectedGroups.includes(ROLLOVER_GROUPS.FORMATIVE) && 'studentFeedbackSettings',
                    ].filter(Boolean)
                );

                if (selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION) && !`${source?.description ?? ''}`.trim()) {
                    resolved.description = true;
                }
                if (selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) && !source?.rubric) {
                    resolved.rubric = true;
                }

                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.GRADING) &&
                    form?.gradingSettings
                ) {
                    resolved.gradingSettings = `(${t('needs-update', 'needs update')})`;
                }
                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.FEEDBACK) &&
                    form?.feedbackSettings?.structure === 'templated'
                ) {
                    resolved.feedbackSettings = `(${t('needs-update', 'needs update')})`;
                }
                if (
                    selectedGroups.includes(ROLLOVER_GROUPS.RUBRIC) &&
                    !selectedGroups.includes(ROLLOVER_GROUPS.FORMATIVE) &&
                    form?.studentFeedbackSettings?.structure === 'templated'
                ) {
                    resolved.studentFeedbackSettings = `(${t('needs-update', 'needs update')})`;
                }
                return resolved;
            });
            if (selectedGroups.includes(ROLLOVER_GROUPS.DESCRIPTION)) {
                setForceRTEUpdate((current) => !current);
            }
            setImportAssignmentSettingsDialog({ open: false });
        },
        [
            form?.feedbackSettings?.structure,
            form?.gradingSettings,
            form?.studentFeedbackSettings?.structure,
            rolloverDisabledGroups,
            onSettingsReplaced,
            setErrors,
            setForm,
            t,
        ]
    );

    const handleResyncAPIAssignment = async () => {
        const course_id = auth.course_id;
        const accessToken = localStorage.getItem('access_token');
        if (course_id) {
            const APIAssignment = await apiService.getAssignment({
                course_id,
                accessToken,
                assignment_id: form.api_assignment_id,
            });
            if (!form.id || form.id !== 'new') {
                setSubmissions([]);
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(assignment.id),
                    await assignmentService.deleteAssignmentBatch(assignment.id),
                ]);
            }

            if (process.env.REACT_APP_KIND === 'D2L') {
                setForm((current) => {
                    return {
                        ...current,
                        api_data: APIAssignment,
                        gradingSettings: {
                            ...current.gradingSettings,
                            submission_types: inferSubmissionTypes(APIAssignment),
                        },
                        api_assignment_id: APIAssignment.Id,
                        title: APIAssignment.Name,
                        description: APIAssignment.CustomInstructions.Html,
                        dueDate: APIAssignment.DueDate,
                        rubric:
                            APIAssignment.Assessment?.Rubrics?.length > 0
                                ? convertD2LRubric(APIAssignment.Assessment.Rubrics[0])
                                : null,
                    };
                });
            } else {
                setForm((current) => {
                    return {
                        ...current,
                        api_data: APIAssignment,
                        gradingSettings: {
                            ...current.gradingSettings,
                            submission_types: inferSubmissionTypes(APIAssignment),
                        },
                        api_assignment_id: APIAssignment.id,
                        title: APIAssignment.name,
                        description: APIAssignment.description,
                        dueDate: APIAssignment.due_at,
                        rubric: APIAssignment.rubric
                            ? convertCanvasRubric(APIAssignment.rubric, APIAssignment.rubric_settings)
                            : null,
                    };
                });
            }
            onSettingsReplaced();
            setForceRTEUpdate((current) => !current);
        }
    };

    useEffect(() => {
        setNode(null);
        return () => setNode(null);
    }, [setNode]);

    const layoutProps = {
        ...props,
        isNew: location.pathname.endsWith('/new'),
        isDraft: Boolean(assignment?.is_draft),
        settingsRevision,
        onSettingsReplaced,
        handleResyncAPIAssignment,
        forceRTEUpdate,
        onClickImportAssignmentSettings: () => setImportAssignmentSettingsDialog({ open: true }),
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <Mobile
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...layoutProps}
                />
            ),
            tablet: (
                <Tablet
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...layoutProps}
                />
            ),
            desktop: (
                <Desktop
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...layoutProps}
                />
            ),
        }[device];
    };

    return isLoading || !form || !submissions ? (
        <Loading />
    ) : (
        <>
            <div key={params.id || 'new'}>{renderResponsiveLayout(device)}</div>
            <SelectRubricDialog
                open={selectRubricDialog.open}
                onClose={closeSelectRubricDialog}
                onClickAddRubric={handleClickAddRubric}
                onClickUseRubric={handleClickUseRubric}
            />
            <CreateRubricDialog
                open={createRubricDialog.open}
                onClose={closeCreateRubricDialog}
                onClickCreateExisting={handleClickCreateExisting}
                onClickCreateAI={handleClickCreateAI}
                onClickCreateManual={handleClickCreateManual}
            />
            <CreateRubricExistingDialog
                open={createRubricExistingDialog.open}
                onClose={closeCreateRubricExistingDialog}
                onCreate={handleSaveRubric}
            />
            <CreateRubricAIDialog
                open={createRubricAIDialog.open}
                title={form.title}
                description={form.description}
                onClose={closeCreateRubricAIDialog}
                onCreate={handleSaveRubric}
            />
            <EditRubricDialog
                open={editRubricDialog.open}
                assignment={form}
                rubric={form.rubric}
                customDefaultForm={editRubricDialog.customDefaultForm}
                onClose={closeEditRubricDialog}
                onSave={handleSaveRubric}
            />
            <UploadContentBankResourcesDialog
                open={uploadContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeUploadContentBankResourcesDialog}
                onClick={onClickUploadContentBankResources}
            />
            <EditContentBankResourcesDialog
                open={editContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeEditContentBankResourcesDialog}
                onSave={handleSaveContentBankResources}
            />
            <EditGradingSettingsDialog
                open={gradingSettingsDialog.open}
                form={form}
                onClose={closeGradingSettingsDialog}
                onSave={handleSaveGradingSettings}
            />
            <FeedbackSettingsDialog
                open={feedbackSettingsDialog.open}
                form={form}
                onClose={closeFeedbackSettingsDialog}
                onSave={handleSaveFeedbackSettings}
            />
            <StudentFeedbackSettingsDialog
                open={studentFeedbackSettingsDialog.open}
                form={form}
                onClose={closeStudentFeedbackSettingsDialog}
                onSave={handleSaveStudentFeedbackSettings}
            />
            <InlineCommentsSettingsDialog
                open={inlineCommentsSettingsDialog.open}
                form={form}
                onClose={closeInlineCommentsSettingsDialog}
                onSave={handleSaveInlineCommentsSettings}
            />
            <PushRubricDialog
                open={pushRubricDialog.open}
                form={pushRubricDialog.form}
                onClose={closePushRubricDialog}
                onSave={pushRubricDialog.onSave}
            />
            <VideoDialog open={videoDialog.open} videoId={videoDialog.videoId} onClose={closeVideoDialog} />
            <ImportAssignmentSettingsDialog
                currentAssignmentId={params.id}
                currentForm={form}
                disabledGroups={rolloverDisabledGroups}
                open={importAssignmentSettingsDialog.open}
                onClose={() => setImportAssignmentSettingsDialog({ open: false })}
                onImport={handleImportAssignmentSettings}
            />
        </>
    );
};

export default PageLayout;
